import { Shimmer, ShimmerElementType } from "@fluentui/react";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { CSSProperties, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import WarningBanner from "../../helper/WarningBanner";
import { IPartnerContainerProps, IPartnerSubscriptionCharts } from "../../interfaces/PartnerView.interfaces";
import { useDropdownWithUrlSync } from "../../SharedComponents/Hooks/useDropdownWithUrlSync";
import { useGetPartnerSubscriptionDataMutation } from "../../slices/ExplorePartners.slice";
import CustomDropdown from "../ProductView/Common/CustomDropdown";
import PartnerSubscriptionChart from "./PartnerSubscriptionCharts";
import PartnerSubscriptionTable from "./PartnerSubscriptionTable";

export default function PartnerSubscriptionContainer(props: IPartnerContainerProps) {
  // Properties
  const [filteredItems, setFilteredItems] = useState<IPartnerSubscriptionCharts[]>([]);
  const [filterChData, setFilterChData] = useState<{ [key: string]: any }>({
    skuFamily: "",
    association: "",
    renewalBucket: ""
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [chartFilter, setChartFilter] = useState<{ filterKey: string; value: any }>({
    filterKey: "",
    value: ""
  });
  const partnerOneID = searchParams.get("partnerOneId");
  const mpnID = searchParams.get("mpnId");

  // Use the reusable hook for dropdown with URL sync
  const { selectedValue: selectedMetric, handleSelection: handleSelectedMetrics } = useDropdownWithUrlSync({
    validValues: ["All SKUs", "Security SKUs"],
    defaultValue: "Security SKUs",
    onValueChange: useCallback(() => {
      // Clear chart filters when metric changes
      setChartFilter({ filterKey: "", value: "" });
      setFilterChData({
        skuFamily: "",
        association: "",
        renewalBucket: ""
      });
      // Clear metric filter URL params to prevent stale filters from being restored
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("mf");
        return newParams;
      });
    }, [setSearchParams])
  });

  const workloadType = selectedMetric === "All SKUs" ? "allup" : "securitysku";
  const [getPartnerSubscriptionData, partnerData] = useGetPartnerSubscriptionDataMutation();

  useEffect(() => {
    if (partnerOneID && mpnID && workloadType) {
      getPartnerSubscriptionData({
        partnerOneID: partnerOneID,
        mpnIdentifier: mpnID,
        workloadIdentifier: workloadType
      });
    }
  }, [partnerOneID, mpnID, workloadType, getPartnerSubscriptionData]);

  // Events

  const showNoData = () => {
    const rootStyle: CSSProperties = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return <div style={rootStyle}>{"Partner's License and Subscription details are not available."}</div>;
  };

  const customProps = {
    partnerOneID: partnerOneID,
    partnerOneName: props?.partnerOneName
  };

  // Hooks
  useEffect(() => {
    logEvent("License And Subscription", "Partner Page", "PartnerSubscriptionContainer", customProps);
  }, [customProps]);

  useEffect(() => {
    setFilteredItems(partnerData?.data?.partnerSubscriptionChartsData ?? []);

    if (partnerData.error) {
      logException(
        "Partner View",
        "PartnerSubscriptionContainer",
        "useGetPartnerProfilePageDataQuery",
        partnerData.error,
        customProps
      );
    }
  }, [partnerData?.data, partnerData.error, partnerData]);

  useEffect(() => {
    const isChartFilterEmpty = chartFilter.filterKey === "" && chartFilter.value === "";

    const filters: { [key: string]: any } = {
      ...filterChData,
      ...(isChartFilterEmpty ? {} : { [chartFilter.filterKey]: chartFilter.value })
    };
    const originalData = partnerData.data?.partnerSubscriptionChartsData || [];

    const filteredData = isChartFilterEmpty
      ? originalData
      : originalData.filter((entry: any) => {
          return Object.entries(filters).every(([key, value]) => {
            if (value === undefined) return true;

            const entryValue = entry[key];

            if (Array.isArray(entryValue)) {
              return entryValue.includes(value);
            }

            return entryValue === value;
          });
        });

    setFilteredItems(filteredData);
  }, [chartFilter, partnerData?.data, filterChData]);

  useEffect(() => {
    if (!chartFilter.filterKey) {
      setFilterChData({
        skuFamily: undefined,
        association: undefined,
        renewalBucket: undefined
      });
    } else {
      setFilterChData((prev) => ({
        ...prev,
        [chartFilter.filterKey]: chartFilter.value
      }));
    }
  }, [chartFilter]);

  const displayViewByMetricSelect = partnerData?.isLoading ? (
    <div style={{ flexBasis: "100%" }}>
      {" "}
      <Shimmer
        shimmerColors={{ shimmer: "var(--shimmer-color)" }}
        styles={{
          root: { paddingTop: 10, paddingBottom: 10, paddingLeft: 10, width: "25%" },
          shimmerWrapper: { ".ms-ShimmerGap-root": { background: "var(--colorBodyBackground1)" } }
        }}
        shimmerElements={[
          { type: ShimmerElementType.line, width: "15%" },
          { type: ShimmerElementType.gap, width: 10 },
          { type: ShimmerElementType.line, width: "15%" }
        ]}
      />{" "}
    </div>
  ) : (
    <FluentProvider theme={webLightTheme}>
      <CustomDropdown
        defaultOption={{
          optionText: selectedMetric,
          optionValue: selectedMetric,
          selectedOptions: [selectedMetric || ""]
        }}
        onSelectedOption={handleSelectedMetrics}
        data={[
          { value: "Security SKUs", text: "Security SKUs" },
          { value: "All SKUs", text: "All SKUs" }
        ]}
        styles={{ root: { flexBasis: "100%", paddingBottom: 10, paddingLeft: 10 } }}
      />
    </FluentProvider>
  );

  if (!partnerData.isLoading && partnerData?.error) {
    return <DisplayAPIsException error={partnerData.error} height={250} />;
  }

  return (
    <>
      {!partnerData.isLoading && filteredItems?.length > 0 && (
        <div style={{ marginLeft: 10, marginRight: 10, marginBottom: 10 }}>
          <WarningBanner message="The information displayed is based on Partner CSP associations" />
        </div>
      )}
      {displayViewByMetricSelect}
      {(((partnerData.isSuccess &&
        Array.isArray(partnerData?.data?.partnerSubscriptionChartsData) &&
        partnerData?.data?.partnerSubscriptionChartsData.length > 0) ||
        partnerData.isLoading) && (
        <PartnerSubscriptionChart
          key={selectedMetric}
          loading={partnerData.isLoading}
          onSelectedChartFilter={setChartFilter}
          filteredData={filteredItems}
          latestMonthTenantsBySkuFamilyKustoLink={partnerData?.data?.latestMonthTenantsBySkuFamilyKustoLink ?? ""}
          latestMonthTotalSeatsBySkuFamilyKustoLink={partnerData?.data?.latestMonthTotalSeatsBySkuFamilyKustoLink ?? ""}
          latestMonthTotalPAUBySkuFamilyKustoLink={partnerData?.data?.latestMonthTotalPAUBySkuFamilyKustoLink ?? ""}
          tenantsTrendBySkuFamilyKustoLink={partnerData?.data?.tenantsTrendBySkuFamilyKustoLink ?? ""}
          totalSeatsTrendBySkuFamilyKustoLink={partnerData?.data?.totalSeatsTrendBySkuFamilyKustoLink ?? ""}
          totalPAUTrendBySkuFamilyKustoLink={partnerData?.data?.totalPAUTrendBySkuFamilyKustoLink ?? ""}
        />
      )) ||
        null}
      {partnerData.isLoading ? (
        <CustomShimmer columnCount={11} />
      ) : (
        (partnerData.isSuccess &&
          Array.isArray(partnerData?.data?.partnerSubscriptionTableData) &&
          partnerData?.data?.partnerSubscriptionTableData.length > 0 && (
            <>
              <div style={{ marginBottom: 10 }}>
                <WarningBanner message="Note: Subscription Table does not support chart filters yet. Chart filters won't filter the data in this view. Stay tuned for more updates!" />
              </div>
              <div style={{ marginLeft: 8, marginRight: 8 }}>
                <PartnerSubscriptionTable
                  tableData={partnerData?.data?.partnerSubscriptionTableData}
                  partnerOneName={props.partnerOneName}
                  kustoLink={partnerData?.data?.partnerSubscriptionTableDataKustoLink ?? ""}
                />
              </div>
            </>
          )) ||
        showNoData()
      )}
    </>
  );
}
